import { Box, styled } from '@mui/material';
import Lottie from 'lottie-react';

import { cloudErrorAnimation, CloudOkIcon, ShieldIcon } from '#shared';

import type { JSX } from 'react';

interface Props {
  icon: 'cloudOk' | 'shield' | 'cloudError';
  largeCaption: string;
  smallCaption: string;
  /** pass "error" to switch border/text color */
  additionalClassName?: string;
  compact?: boolean;
}

const PREFIX = 'TSMED-InfoTile';
export const infoTileClasses = {
  root: `${PREFIX}-root`,
  icon: `${PREFIX}-icon`,
  captions: `${PREFIX}-captions`,
  largeCaption: `${PREFIX}-largeCaption`,
  smallCaption: `${PREFIX}-smallCaption`,
  error: `${PREFIX}-error`,
} as const;

type RootProps = {
  compact?: boolean;
};

const Root = styled(Box, {
  name: PREFIX,
  slot: 'Root',
  overridesResolver: (props, styles) => styles.root,
  shouldForwardProp: prop => prop !== 'compact',
})<RootProps>(
  ({ theme: { spacing, palette, utils, breakpoints }, compact }) => ({
    [`&.${infoTileClasses.root}`]: {
      display: 'inline-flex',
      border: `1px solid ${palette.grey[700]}`,
      borderRadius: 10,
      textAlign: 'center',
      justifyContent: 'center',
      alignItems: 'center',
      verticalAlign: 'top',
      padding: compact ? spacing(1) : spacing(3, 6),
      width: '100%',
      height: '100%',

      [breakpoints.down('md')]: {
        padding: spacing(1, 3),
      },
    },

    [`.${infoTileClasses.icon}`]: {
      margin: spacing(0, 1.25),
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      maxWidth: '100%',
      maxHeight: '100%',

      svg: {
        display: 'block',
        width: compact ? '60%' : '100%',
        height: compact ? '60%' : '100%',
      },
      img: {
        display: 'block',
        width: compact ? '60%' : '100%',
        height: compact ? '60%' : '100%',
      },
      canvas: {
        width: compact ? '60%' : '100%',
        height: compact ? '60%' : '100%',
      },
    },

    [`.${infoTileClasses.captions}`]: {
      color: palette.grey[700],
      fontWeight: 600,
      textAlign: 'left',
    },

    [`.${infoTileClasses.largeCaption}`]: {
      fontSize: utils.clamp(compact ? 18 : 20, compact ? 20 : 36, 'md-4k'),
      lineHeight: 1.1,
    },

    [`.${infoTileClasses.smallCaption}`]: {
      fontSize: utils.clamp(compact ? 14 : 16, compact ? 20 : 26, 'md-4k'),
      lineHeight: 1.1,
    },

    // error modifier
    [`&.${infoTileClasses.error}`]: {
      borderColor: palette.error[500],
      [`.${infoTileClasses.captions}`]: { color: palette.error[500] },
    },
  }),
);

const icons = (compact?: boolean): Record<Props['icon'], JSX.Element> => ({
  shield: <ShieldIcon />,
  cloudOk: <CloudOkIcon />,
  cloudError: (
    <Lottie
      animationData={cloudErrorAnimation}
      loop
      style={{
        width: compact ? '60%' : '100%',
        height: compact ? '60%' : '100%',
      }}
    />
  ),
});

export const InfoTile = ({
  icon,
  largeCaption,
  smallCaption,
  additionalClassName = '',
  compact,
}: Props) => {
  const isError = additionalClassName === 'error';

  return (
    <Root
      className={`${infoTileClasses.root} ${isError ? infoTileClasses.error : ''}`}
      compact={compact}
    >
      <span className={infoTileClasses.icon}>{icons(compact)[icon]}</span>

      <span className={infoTileClasses.captions}>
        <div className={infoTileClasses.largeCaption}>{largeCaption}</div>
        <div className={infoTileClasses.smallCaption}>{smallCaption}</div>
      </span>
    </Root>
  );
};
